/**
 * Form page for creating or editing a bounty (mode: "create" | "edit",
 * type: "normal" | "ai"). Rendered at "/account/create" (create/normal, via
 * pages/user/UserHomepage) and from admin screens (AdminListViewContainer for
 * edits, SearchAdminPage for AI bounty creation).
 * Manages title/description/category/sex/type/price-range/reward/match-type
 * state plus an ImagePicker image map; validates input, then calls
 * UserBountyAPI (createBounty/updateBounty/deleteBounty + image endpoints) or
 * AdminAPI for AI/admin flows. Uses UserContext, ErrorContext, SubheadedPage.
 */
import { useState, useEffect, Fragment } from "react";
import { SubheadedPage } from "@/components/SubheadedPage";
import { Pencil, Save, Send, Trash } from "lucide-react";
import InputField, { FieldConfig } from "@/components/InputField";
import { Category, Categories, Sex, FullCategories, Sexes } from "@/types/category";
import Dropdown from "@/components/Dropdown";
import RangeField from "@/components/RangeField";
import ImagePicker, { ImageMap } from "@/components/ImagePicker";
import { UserBountyAPI } from "@/api/userBounty";
import { useImmer } from "use-immer";
import { useUserState } from "@/contexts/UserContext";
import {
    Bounty,
    MatchTypes,
    MatchType,
    bountyTypeTooltipMessage,
    BOUNTY_TITLE_MAX_LENGTH,
    BOUNTY_DESCRIPTION_MAX_LENGTH,
} from "@/types/bounty";
import { useNavigate } from "react-router-dom";
import {
    bountyRewardMessage,
    bountyDescriptionMessage,
    bountyTitleMessage,
    bountyImageEmptyText,
} from "@/constants/bounty";
import { FIELD_WIDTH, BUTTON_SIZE } from "@/constants/ui";
import LoadingSpinner from "@/components/LoadingSpinner";
import AlertDialog from "@/components/AlertDialog";
import { useError } from "@/contexts/ErrorContext";
import { AdminAPI } from "@/api/admin";
import { AiBounty } from "@/types/aiBounty";

export type BountyCreateEditMode = "create" | "edit";
export type BountyCreateEditType = "normal" | "ai";

type BountyCreateEditProps = {
    mode: BountyCreateEditMode;
    type: BountyCreateEditType;
    bounty?: AiBounty | Bounty;
    onDelete?: () => void;
};

const BountyCreateEdit: React.FC<BountyCreateEditProps> = ({ mode, type, bounty }) => {
    const navigate = useNavigate();
    const user = useUserState();
    const [imagesToDelete, setImagesToDelete] = useImmer<string[]>([]);
    const [showImagePicker, setShowImagePicker] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const { showError } = useError();
    const [success, setSuccess] = useState<boolean>(false);
    const [showEmailDialog, setShowEmailDialog] = useState<boolean>(false);

    if (mode === "edit" && !bounty) {
        throw new Error("Bounty is required when mode is 'edit'");
    }

    const createImageMapFromBounty = (bounty: Bounty | AiBounty) => {
        const map = new Map<string, File | string | null>();
        for (const url of bounty.s3ImageUrls) {
            map.set(url, url);
        }
        return map;
    };

    const [imageMap, setImageMap] = useImmer<ImageMap>(() => {
        if (mode === "edit") {
            if (!bounty) {
                throw new Error("Edit mode requires a bounty");
            }
            return createImageMapFromBounty(bounty);
        }
        return new Map<string, File | string | null>();
    });

    const [originalBounty, setOriginalBounty] = useState<Bounty | AiBounty | undefined>(() => {
        if (mode === "edit") {
            if (!bounty) {
                throw new Error("Edit mode requires a bounty");
            }
            return bounty;
        }
        return undefined;
    });

    const [title, setTitle] = useState(mode === "edit" ? originalBounty!.title : "");
    const [description, setDescription] = useState(
        mode === "edit" ? originalBounty!.description : ""
    );
    const [category, setCategory] = useState<Category | null>(
        mode === "edit" ? originalBounty!.category : null
    );
    const [categoryType, setCategoryType] = useState<string | null>(
        mode === "edit" ? originalBounty!.categoryType : null
    );
    const [sex, setSex] = useState<Sex | null>(mode === "edit" ? originalBounty!.sex : null);
    const [minPrice, setMinPrice] = useState<number | null>(
        mode === "edit" ? originalBounty!.minPrice : null
    );
    const [maxPrice, setMaxPrice] = useState<number | null>(
        mode === "edit" ? originalBounty!.maxPrice : null
    );
    const [reward, setReward] = useState<number | null>(
        mode === "edit" && type === "normal" ? (originalBounty as Bounty).reward : null
    );
    const [matchType, setMatchType] = useState<MatchType | null>(
        mode === "edit" && originalBounty!.s3ImageUrls.length > 0 ? originalBounty!.type : "exact"
    );

    useEffect(() => {
        if (!user?.email) {
            setShowEmailDialog(true);
        }
    }, []);

    // Track previous image count to detect when images are added/removed
    const [prevImageCount, setPrevImageCount] = useState(imageMap.size);
    useEffect(() => {
        if (imageMap.size > 0 && prevImageCount === 0) {
            // Image was just added - require user to select match type
            setMatchType(null);
        } else if (imageMap.size === 0 && prevImageCount > 0) {
            // All images removed - default back to exact
            setMatchType("exact");
        }
        setPrevImageCount(imageMap.size);
    }, [imageMap.size, prevImageCount]);

    useEffect(() => {
        if (mode === "edit" && originalBounty) {
            setTitle(originalBounty.title);
            setDescription(originalBounty.description);
            setCategory(originalBounty.category);
            setCategoryType(originalBounty.categoryType);
            setSex(originalBounty.sex);
            setMinPrice(originalBounty.minPrice);
            setMaxPrice(originalBounty.maxPrice);
            setReward(type === "normal" ? (originalBounty as Bounty).reward : null);
            setImageMap(createImageMapFromBounty(originalBounty));
            setMatchType(originalBounty.type);
            setImagesToDelete([]);
        } else {
            setTitle("");
            setDescription("");
            setCategory(null);
            setCategoryType(null);
            setSex(null);
            setMinPrice(null);
            setMaxPrice(null);
            setReward(null);
            setMatchType("exact");
            setImageMap(new Map<string, File | string | null>());
        }
    }, [mode, originalBounty]);

    const titleConfig: FieldConfig = {
        key: "title",
        label: "title",
        type: "text",
        placeholder: bountyTitleMessage,
        readonly: false,
        readonlyValue: null,
        fieldState: title,
        setFieldState: setTitle,
    };

    const descriptionConfig: FieldConfig = {
        key: "description",
        label: "description",
        type: "textarea",
        placeholder: bountyDescriptionMessage,
        readonly: false,
        readonlyValue: null,
        fieldState: description,
        setFieldState: setDescription,
    };

    const rewardConfig: FieldConfig = {
        key: "reward",
        label: "reward (min $5)",
        type: "number",
        readonly: false,
        readonlyValue: null,
        fieldState: reward,
        setFieldState: setReward,
        integerOnly: true,
        min: 5,
        max: 500,
    };

    function resetForm() {
        setTitle("");
        setDescription("");
        setCategory(null);
        setCategoryType(null);
        setSex(null);
        setMinPrice(null);
        setMaxPrice(null);
        setMatchType("exact");
        setReward(null);
        setImageMap(new Map<string, File | string | null>());
    }

    function resetStateAndShowSuccess() {
        if (mode === "edit") {
            throw new Error("Cannot reset state for edit mode");
        }
        setSuccess(true);
    }

    const getBountyFromFields = () => {
        let bountyFromFields: Bounty = {
            title: title.trim(),
            description: description.trim(),
            category: category!,
            categoryType,
            sex: sex,
            minPrice: minPrice!,
            maxPrice: maxPrice!,
            reward: reward!,
            s3ImageUrls: [],
            porsuiUserId: user!.id,
            type: matchType ?? "exact",
        };
        if (originalBounty) {
            bountyFromFields.id = originalBounty.id;
        }
        return bountyFromFields;
    };

    function getFormDataFromImageMap() {
        const formData = new FormData();
        for (const file of imageMap.values()) {
            // Only append File objects (new uploads), not existing URL strings
            if (file instanceof File) {
                formData.append("images", file);
            }
        }
        if (mode === "edit") {
            for (const imageUrl of imagesToDelete) {
                formData.append("imagesToDelete", imageUrl);
            }
        }
        return formData;
    }

    const handleCreateClick = async () => {
        if (!checkIfAllInputValid()) {
            return;
        }
        setLoading(true);
        if (type === "normal") {
            try {
                const { bounty } = await UserBountyAPI.createBounty(
                    user!.id,
                    getBountyFromFields()
                );
                try {
                    await UserBountyAPI.uploadBountyImages(
                        user!.id,
                        bounty.id!,
                        getFormDataFromImageMap()
                    );
                    resetStateAndShowSuccess();
                } catch (error: any) {
                    showError(error as string);
                } finally {
                    setLoading(false);
                }
            } catch (error: any) {
                showError(error as string);
            } finally {
                setLoading(false);
            }
        } else if (type === "ai") {
            try {
                const { aiBounty } = await AdminAPI.createAiBounty(getBountyFromFields());
                console.log(aiBounty);
            } catch (error: any) {
                showError(error as string);
            }
        }
    };

    const handleUpdateClick = async () => {
        if (!checkIfAllInputValid()) {
            return;
        }
        setLoading(true);
        try {
            const { bounty } = await UserBountyAPI.updateBounty(user!.id, getBountyFromFields());
            const { bounty: updatedBounty } = await UserBountyAPI.editBountyImages(
                user!.id,
                bounty.id!,
                getFormDataFromImageMap()
            );
            setOriginalBounty(updatedBounty);
            setSuccess(true);
        } catch (error: any) {
            showError(error as string);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = async () => {
        if (!originalBounty || mode === "create") {
            throw new Error("Cannot delete bounty in create mode");
        }
        setLoading(true);
        try {
            if (!user?.isAdmin) {
                await UserBountyAPI.deleteBounty(user!.id, originalBounty!.id!);
                navigate("/account/listings");
            } else {
                // User type is admin
                if (type === "normal") {
                    await AdminAPI.deleteBounty(originalBounty!.id!);
                    navigate("/admin/bounties");
                } else {
                    await AdminAPI.deleteAiBounty(originalBounty!.id!);
                    navigate("/admin/search?tab=ai-bounties");
                }
            }
        } catch (error: any) {
            showError(error as string);
        } finally {
            setLoading(false);
        }
    };

    function allFieldsAreFilled(category: keyof typeof FullCategories): boolean {
        let needsSex: boolean = false;
        let needsType: boolean = false;
        const val = FullCategories[category];
        if (Array.isArray(val)) {
            if (val.includes("male")) {
                needsSex = true;
            } else {
                needsType = true;
            }
        } else if (typeof val === "object") {
            needsSex = true;
            needsType = true;
        } else {
            throw new Error("Invalid category type");
        }

        let hasAllCategories: boolean = false;
        if (needsSex && needsType) {
            hasAllCategories = sex !== null && categoryType !== null;
        } else if (needsSex) {
            hasAllCategories = sex !== null;
        } else if (needsType) {
            hasAllCategories = categoryType !== null;
        } else {
            hasAllCategories = false;
        }

        if (hasAllCategories && title && description && minPrice && maxPrice) {
            return true;
        }

        return false;
    }

    function checkIfAllInputValid(): boolean {
        const errors: string[] = [];

        if (title.trim().length < 5) {
            errors.push("title must be at least 5 characters");
        }
        if (description.trim().length < 15) {
            errors.push(
                "description must be at least 15 characters, it's important to be detailed"
            );
        }
        if (minPrice! >= maxPrice!) {
            errors.push("min price must be less than max price");
        }
        if (reward! < 5) {
            errors.push("reward must be at least $5");
        }

        if (errors.length > 0) {
            const errorMessage = errors.join("\n");
            console.log(errorMessage);
            showError(errorMessage);
            return false;
        }

        return true;
    }

    const renderSecondDropdown = (category: keyof typeof FullCategories) => {
        let firstDropdownType: "sex" | "type";
        const val = FullCategories[category];
        if (Array.isArray(val)) {
            if (val.includes("male")) {
                firstDropdownType = "sex";
            } else {
                firstDropdownType = "type";
            }
        } else if (typeof val === "object") {
            firstDropdownType = "sex";
        } else {
            throw new Error("Invalid category type");
        }

        const isSex: boolean = firstDropdownType === "sex";
        const choices = isSex ? Sexes : Array.isArray(val) ? val : Object.values(val);
        const choice = isSex ? sex : categoryType;
        const setChoice = isSex ? setSex : setCategoryType;
        const label = isSex ? "sex" : `${category} type`;
        const placeholder = isSex ? "select sex" : `select a ${category} type`;

        return (
            <Dropdown
                id="firstDropdown"
                label={label}
                placeholder={placeholder}
                choices={choices}
                choice={choice}
                setChoice={setChoice}
                className="mb-4"
            />
        );
    };

    const renderSexSpecificDropdown = (sex: string, category: keyof typeof FullCategories) => {
        const val = FullCategories[category];
        const types: string[] = (val as Record<string, any>)[sex];

        if (!types) {
            // This is for watches which only has sex
            return null;
        }

        return (
            <Dropdown
                id="firstDropdown"
                label={`${category} type`}
                placeholder={`select a ${category} type`}
                choices={types}
                choice={categoryType}
                setChoice={setCategoryType}
                className="mb-4"
            />
        );
    };

    const hasChanges = () => {
        if (mode === "create") {
            throw new Error("Cannot check for changes in create mode");
        }
        if (imagesToDelete.length > 0) {
            return true;
        }
        for (const image of imageMap.values()) {
            if (image instanceof File) {
                return true;
            }
        }
        let changed: boolean =
            title !== originalBounty!.title ||
            description !== originalBounty!.description ||
            category !== originalBounty!.category ||
            sex !== originalBounty!.sex ||
            categoryType !== originalBounty!.categoryType ||
            minPrice !== originalBounty!.minPrice ||
            maxPrice !== originalBounty!.maxPrice ||
            matchType !== originalBounty!.type;

        if (type === "normal") {
            changed = changed || reward !== (originalBounty as Bounty).reward;
        }

        return changed;
    };

    const renderButtons = () => {
        if (mode === "create") {
            return (
                <button
                    className={`p-2 rounded-full text-black font-porsui hover:bg-slate-100 transition-colors ${
                        category !== null && allFieldsAreFilled(category) && !loading
                            ? "text-black"
                            : "text-slate-400 cursor-not-allowed"
                    }`}
                    onClick={handleCreateClick}
                    disabled={loading || category === null || !allFieldsAreFilled(category)}
                >
                    <Send size={BUTTON_SIZE} />
                </button>
            );
        } else if (mode === "edit") {
            return (
                <Fragment>
                    <button
                        className={`p-2 rounded-full text-black font-porsui items-center ${
                            category !== null &&
                            allFieldsAreFilled(category) &&
                            !loading &&
                            hasChanges()
                                ? "text-black"
                                : "text-slate-400 cursor-not-allowed"
                        }`}
                        onClick={handleUpdateClick}
                        disabled={
                            loading ||
                            category === null ||
                            !allFieldsAreFilled(category) ||
                            !hasChanges()
                        }
                    >
                        <Save size={BUTTON_SIZE} />
                    </button>
                    <button
                        className={`p-2 rounded-full text-black font-porsui items-center ${
                            loading ? "text-slate-400 cursor-not-allowed" : "text-black"
                        }`}
                        onClick={handleDeleteClick}
                        disabled={loading}
                    >
                        <Trash size={BUTTON_SIZE} />
                    </button>
                </Fragment>
            );
        } else {
            throw new Error("Invalid bounty create edit type");
        }
    };

    return (
        <SubheadedPage
            heading={`${mode === "create" ? "post bounty" : "edit bounty"}`}
            headerAction={
                <div className="w-full relative">
                    <div className="absolute top-[-24px] right-0 flex flex-row space-x-4">
                        {renderButtons()}
                    </div>
                </div>
            }
            headerColor="bg-slate-50"
        >
            {/* Loading overlay */}
            {(loading || success) && (
                <div className="absolute inset-0 bg-slate-50/80 z-40 flex items-center justify-center">
                    <LoadingSpinner
                        success={success}
                        message={success ? undefined : "processing bounty"}
                        successMessage={`bounty ${
                            mode === "create" ? "created" : "updated"
                        } successfully!`}
                        onSuccessComplete={() => {
                            setSuccess(false);
                            if (mode === "create") {
                                resetForm();
                            }
                        }}
                    />
                </div>
            )}
            {showImagePicker && (
                <div className="md:fixed md:inset-0 flex md:items-center justify-center bg-black bg-opacity-50 z-50 overflow-auto">
                    <ImagePicker
                        imageMap={imageMap}
                        maxImages={6}
                        setImageMap={setImageMap}
                        onClose={() => setShowImagePicker(false)}
                        setImagesToDelete={setImagesToDelete}
                        type={mode}
                    />
                </div>
            )}
            <div className={`flex flex-col ${showImagePicker ? "opacity-50" : ""}`}>
                <div className="w-full flex flex-col items-center md:flex-row md:items-start">
                    {/* Image Section */}
                    <div className="h-full flex flex-col md:mx-8 mt-1 justify-between space-y-2 items-center md:items-start">
                        {/* Bounty Image Section */}
                        <div className="relative w-72 h-72">
                            {imageMap.size > 0 ? (
                                <img
                                    src={imageMap.keys().next().value}
                                    alt="bounty photo"
                                    className="w-full h-full object-cover rounded-xl"
                                />
                            ) : (
                                <div className="w-72 h-72 rounded-xl shadow-md overflow-hidden bg-gray-100">
                                    <div className="flex flex-col items-center justify-center h-full p-8">
                                        <span className="text-slate-500 font-porsui text-sm text-center whitespace-pre-line">
                                            {bountyImageEmptyText}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="absolute bottom-[-5px] right-[-5px] p-2 rounded-full bg-white shadow-md cursor-pointer hover:bg-gray-50 transition-colors">
                                <Pencil
                                    className="w-5 h-5 text-black"
                                    onClick={() => setShowImagePicker(true)}
                                />
                            </div>
                        </div>
                        {imageMap.size > 0 && (
                            <Dropdown
                                key="bountyType"
                                id="bountyType"
                                label="match type"
                                placeholder="select the type"
                                choices={MatchTypes}
                                choice={matchType}
                                setChoice={setMatchType}
                                tooltipMessage={bountyTypeTooltipMessage("you")}
                                className="md:w-full w-64"
                            />
                        )}
                    </div>
                    <div
                        className={`${FIELD_WIDTH} h-full flex flex-col justify-between space-y-2`}
                    >
                        {/* Title Field */}
                        <InputField
                            key="title"
                            fieldConfig={titleConfig}
                            characterLimit={BOUNTY_TITLE_MAX_LENGTH}
                        />
                        {/* Description Text Area */}
                        <InputField
                            key="description"
                            fieldConfig={descriptionConfig}
                            characterLimit={BOUNTY_DESCRIPTION_MAX_LENGTH}
                            height="h-44"
                        />
                        {/* Price Range */}
                        <RangeField
                            key="range"
                            label="price range"
                            min={minPrice}
                            setMin={setMinPrice}
                            max={maxPrice}
                            setMax={setMaxPrice}
                        />
                    </div>
                    <div
                        className={`${FIELD_WIDTH} h-full flex flex-col justify-start md:ml-8 mt-2 md:mt-0`}
                    >
                        {/* Top Level Category Dropdown */}
                        <Dropdown
                            key="category"
                            id="category"
                            label="category"
                            placeholder="select a category"
                            choices={Categories}
                            choice={category}
                            setChoice={(e) => {
                                setSex(null);
                                setCategoryType(null);
                                setCategory(e.toString() as Category);
                            }}
                            className="mb-4"
                        />
                        {category && renderSecondDropdown(category)}
                        {category && sex && renderSexSpecificDropdown(sex, category)}
                        {/* Reward Field */}
                        {type === "normal" && (
                            <>
                                <InputField key="reward" fieldConfig={rewardConfig} />
                                <div className="w-64 mt-1 text-xs text-zinc-500 font-porsui flex flex-col text-center items-center space-y-0.5">
                                    <span>{bountyRewardMessage}</span>
                                    <span>8% will be added for processing fees</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex flex-col w-full text-center items-center p-4 justify-center mt-4">
                    {showEmailDialog && (
                        <AlertDialog
                            title="email required"
                            message="please enter your email in order to post a bounty"
                            cancelText="not now"
                            confirmText="ok"
                            onConfirm={() => {
                                setShowEmailDialog(false);
                                navigate("/verifyEmail");
                            }}
                            onCancel={() => {
                                setShowEmailDialog(false);
                                navigate("/account/profile");
                            }}
                        />
                    )}
                </div>
            </div>
        </SubheadedPage>
    );
};

export default BountyCreateEdit;
